import { FormEvent, useState } from 'react';
import { clearSession, loginService, saveSession } from '../../../services/session/loginService';
import { getUserById } from '../../../services/user';
import { useUserStore } from '../../../stores';
import { useAuthStore } from '../../../stores/auth-store';

export interface UseLoginOptions {
	/** valor inicial de "Manter-me conectado" */
	rememberMeDefault?: boolean;
}

/**
 * Fluxo de login compartilhado pelas paginas publicas (home e pagina de login).
 *
 * Usa os mesmos servicos/stores do restante da aplicacao:
 * loginService -> saveSession -> getUserById -> useUserStore/useAuthStore.
 * Ao concluir, recarrega a aplicacao na raiz do tenant para que tudo que
 * depende da sessao (SignalR, header, etc.) inicialize com o token presente.
 */
export const useLogin = ({ rememberMeDefault = false }: UseLoginOptions = {}) => {
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [rememberMe, setRememberMe] = useState(rememberMeDefault);
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);

	const { setUser } = useUserStore();
	const { setAuthenticated } = useAuthStore();

	const submit = async (event?: FormEvent) => {
		event?.preventDefault();
		setError('');

		if (!email.trim()) {
			setError('Informe o e-mail.');
			return;
		}

		if (!password) {
			setError('Informe a senha.');
			return;
		}

		setLoading(true);

		try {
			const loginData = await loginService({ email: email.trim(), password });
			saveSession(loginData, { persist: rememberMe });

			const userData = await getUserById({ id: loginData.sid });
			if (!userData) {
				clearSession();
				setError('Erro ao carregar dados do usuário.');
				return;
			}

			setUser(userData);
			setAuthenticated(true);

			window.location.replace(`${process.env.PUBLIC_URL || ''}/`);
		} catch (err: any) {
			// garante que uma sessao parcialmente salva nao fique pendurada
			clearSession();
			const msg =
				err?.response?.data?.error_description ||
				err?.response?.data?.message ||
				'E-mail ou senha inválidos. Tente novamente.';
			setError(msg);
		} finally {
			setLoading(false);
		}
	};

	return {
		email,
		setEmail,
		password,
		setPassword,
		rememberMe,
		setRememberMe,
		error,
		setError,
		loading,
		submit,
	};
};
